import React, { type ReactNode, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import {
	Box,
	Flex,
	Grid,
	Inline,
	Pressable,
	type PressableProps,
	Stack,
	xcss,
	// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
} from '@atlaskit/primitives';
import Tabs, { TabList, useTab } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { NounKeys, NounKeys as NounKeysValue, nouns, NOUNS_ORDER } from '../../../constants/nouns';
import {
	ProductKeys,
	ProductKeys as ProductKeysObject,
	PRODUCTS_ORDER,
	useProductConfigs,
} from '../../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import { type OptionType } from '../../../constants/types';
import { type TriggerComponentProps } from '../../../types';
import { isProductSmartlink } from '../../../utils/connectors';
import { OAuthSource } from '../../../utils/events/oauth-events';
import { getWorkspaceNameFromProduct } from '../../../utils/get-workspace-name-from-product';
import {
	OAuthWarningWrapper,
	type UseIsUserOAuthed,
	type UseOAuthHandlers,
} from '../../oauth-warning-wrapper';
import { ProductOrNounIcon } from '../../product-icon';
import { Hide } from '../../width-observer';

import { messages } from './messages';

/** The maximum number of visible tabs.
 * Higher number of app options will be rendered in a menu.
 */
const APP_OPTION_OVERFLOW = 4;

type OptionValue = ProductKeys | NounKeys;
type InlineAppOptionType = { inlineLabel?: React.ReactNode; inlineIsSelected?: boolean };

type FilterDataOption = {
	queryParamValue: string;
	isSelected: boolean;
};
type AppOptionType = OptionType<OptionValue> & InlineAppOptionType;

interface AppFilterTabsInlineProps {
	availableProducts: ProductKeys[];
	availableNouns: NounKeys[];
	selectedProduct?: ProductKeys;
	onSelectProduct: (productKey: ProductKeys | undefined) => void;
	filterData?: {
		options: FilterDataOption[];
	};
	setAtlasNoun: (noun?: NounKeys) => void;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}

/**
 * The app filter.
 * Shows available products in tabs.
 */
const AppFilterTabsInline = ({
	onSelectProduct,
	availableProducts,
	availableNouns,
	selectedProduct,
	filterData,
	setAtlasNoun,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
}: AppFilterTabsInlineProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	const isWebCrawlerAvailable = availableProducts.includes(ProductKeysObject.AtlassianWebCrawler);
	// Convoluting atlassian-web-crawler with nouns is a tech-debt, ideally it should be decoupled from nouns.
	const shouldShowNouns: boolean = availableNouns.length > 0 || isWebCrawlerAvailable;

	const availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable = shouldShowNouns
		? availableProducts.filter(
				(key) => key !== ProductKeysObject.Atlas && key !== ProductKeysObject.AtlassianWebCrawler,
			)
		: availableProducts;

	const { inlineOptions, nestedOptions } = useMemo((): {
		inlineOptions: AppOptionType[];
		nestedOptions: AppOptionType[];
	} => {
		let availableOptions: AppOptionType[] = [
			...availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable,
		]
			.filter((productKey) => products[productKey])
			.sort((a, b) => {
				const indexA = PRODUCTS_ORDER.indexOf(a);
				const indexB = PRODUCTS_ORDER.indexOf(b);

				if (indexA === -1 || indexB === -1) {
					return 0;
				} else {
					return indexA - indexB;
				}
			})
			.map((productKey) => {
				const workspaceName = getWorkspaceNameFromProduct(productKey, thirdPartyConfigs);
				const label = workspaceName || products[productKey].displayName;

				const isSelected = productKey === selectedProduct;
				return {
					label,
					inlineLabel: label,
					value: productKey,
					isSelected,
					inlineIsSelected: isSelected,
				};
			});

		if (shouldShowNouns) {
			const nounsToDisplay = [
				...availableNouns.filter((noun) => noun !== NounKeys.AtlassianWebCrawler),
				...(isWebCrawlerAvailable ? [ProductKeys.AtlassianWebCrawler] : []),
			].sort((a, b) => NOUNS_ORDER.indexOf(a) - NOUNS_ORDER.indexOf(b));

			const availableNounOptions = nounsToDisplay.map((noun) => {
				const isAtlasSelected = selectedProduct === ProductKeysObject.Atlas;
				const isWebCrawlerSelected = selectedProduct === ProductKeysObject.AtlassianWebCrawler;

				const isSelected =
					((isWebCrawlerSelected && noun === NounKeysValue.AtlassianWebCrawler) ||
						(isAtlasSelected &&
							filterData?.options.find((option) => option.queryParamValue === noun)?.isSelected)) ??
					false;
				const label = formatMessage(nouns[noun].displayNamePlural);
				return {
					label,
					inlineLabel: label,
					value: noun,
					isSelected,
					inlineIsSelected: isSelected,
				};
			});

			availableOptions.push(...availableNounOptions);
		}

		return splitAppOptions(
			availableOptions,
			availableOptions.findIndex((option) => option.isSelected),
		);
	}, [
		availableNouns,
		availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable,
		filterData?.options,
		formatMessage,
		isWebCrawlerAvailable,
		products,
		selectedProduct,
		shouldShowNouns,
		thirdPartyConfigs,
	]);

	const inlineSelectedIndex: number = useMemo(() => {
		return inlineOptions.findIndex((option) => option.isSelected);
	}, [inlineOptions]);

	const onSelectTab = (productOrNounKey?: ProductKeys | NounKeys) => {
		if (!productOrNounKey || productOrNounKey in products) {
			onSelectProduct(productOrNounKey as ProductKeys);
		} else if (productOrNounKey in nouns) {
			setAtlasNoun(productOrNounKey as NounKeys);
		}
	};

	return (
		<Inline space="space.100" alignBlock="center" grow="fill">
			<Tabs
				id="app-filters-tabs"
				selected={inlineSelectedIndex + 1} // Because the "All" takes the 0 spot
				onChange={(tabIndex) => {
					if (tabIndex === 0) {
						onSelectTab(undefined); // user selected "All"
						return;
					}
					const changedOption = inlineOptions[tabIndex - 1]; // index 0 is "All"
					if (!changedOption) {
						return;
					}
					onSelectTab(changedOption.value);
				}}
			>
				<TabList>
					{/* All option */}
					<FilterTab
						productOrNounKey={undefined}
						aria-label={formatMessage(messages.allOptionAccessibleLabel)}
					>
						<FormattedMessage {...messages.allOptionVisualLabel} />
					</FilterTab>
					{/* Product option e.g. Confluence, Google Drive */}
					{inlineOptions.map((option) => {
						const isSmartlink = isProductSmartlink({ productKey: option.value, thirdPartyConfigs });

						return (
							<OAuthWarningWrapper
								datasourceId={option.datasourceId}
								isInline
								key={option.value}
								product={option.value}
								trigger={({ oAuthWarning, isDisabled, triggerProps }: TriggerComponentProps) => (
									<FilterTab
										triggerProps={triggerProps}
										key={option.value}
										isDisabled={isDisabled}
										productOrNounKey={option.value}
									>
										{option.inlineLabel}
										{oAuthWarning}
									</FilterTab>
								)}
								useOAuthHandlers={useOAuthHandlers}
								useIsUserOAuthed={useIsUserOAuthed}
								isSmartlink={isSmartlink}
								oAuthSource={OAuthSource.searchPageFilter}
								thirdPartyConfigs={thirdPartyConfigs}
							/>
						);
					})}
					{/* More menu button trigger */}
					{nestedOptions.length > 0 && (
						<MoreAppsButton
							nestedOptions={nestedOptions}
							onSelectTab={onSelectTab}
							useOAuthHandlers={useOAuthHandlers}
							useIsUserOAuthed={useIsUserOAuthed}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
					)}
				</TabList>
			</Tabs>
		</Inline>
	);
};

const tabsTypography = xcss({
	color: 'color.text.subtlest',
	font: token('font.body'),
});

const filterTabStyles = xcss({
	background: 'none',
	':hover': {
		background: 'none',
	},
});

const filterTextStyles = xcss({
	display: 'flex',
	paddingRight: 'space.025',
});

const filterNounTestStyles = xcss({
	paddingBottom: 'space.025',
});

const moreAppsMenuStyles = xcss({
	width: '330px',
});

const moreAppsMenuButtonStyles = xcss({
	width: '100%',
});

interface FilterTabProps extends PressableProps {
	productOrNounKey?: ProductKeys | NounKeys;
	children: ReactNode;
	isDisabled?: boolean;
	oAuthWarning?: React.ReactNode;
	triggerProps?: TriggerProps;
}

const FilterTab = ({
	productOrNounKey,
	children,
	isDisabled: isOnClickDisabled,
	triggerProps,
	...pressableProps
}: FilterTabProps) => {
	const tabAttrs = useTab();
	const onClick = isOnClickDisabled ? () => {} : tabAttrs.onClick;
	const isNounFilter = !!productOrNounKey && !!nouns[productOrNounKey];
	return (
		<Pressable
			xcss={[filterTabStyles, tabsTypography]}
			{...pressableProps}
			{...tabAttrs}
			onClick={onClick}
			{...triggerProps}
			aria-controls={undefined}
		>
			<Flex alignItems="center" gap="space.050">
				<>
					{productOrNounKey && (
						<ProductOrNounIcon label="" size="small" productOrNounKey={productOrNounKey} />
					)}
					<Box xcss={[isNounFilter && filterNounTestStyles, filterTextStyles]}>{children}</Box>
				</>
			</Flex>
		</Pressable>
	);
};

const MoreAppsMenu = ({
	options,
	onSelectTab,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
}: {
	options: AppOptionType[];
	onSelectTab: (productOrNounKey: ProductKeys | NounKeys | undefined) => void;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}) => {
	return (
		<Box xcss={moreAppsMenuStyles} padding="space.100">
			<Stack space="space.050">
				{options.map((option) => {
					const isSmartlink = isProductSmartlink({ productKey: option.value, thirdPartyConfigs });

					return (
						<OAuthWarningWrapper
							datasourceId={option.datasourceId}
							product={option.value}
							trigger={({ isDisabled, oAuthWarning, triggerProps }: TriggerComponentProps) => (
								<Box xcss={moreAppsMenuButtonStyles}>
									<Button
										shouldFitContainer
										key={option.value}
										appearance="subtle"
										onClick={() => {
											!isDisabled && onSelectTab(option.value);
										}}
										iconBefore={() => (
											<ProductOrNounIcon label="" size="small" productOrNounKey={option.value} />
										)}
										{...triggerProps}
									>
										<Grid templateColumns="1fr auto" alignItems="center">
											<Inline alignInline="start">
												<Box paddingInlineStart="space.050">{option.label}</Box>
											</Inline>
											{oAuthWarning}
										</Grid>
									</Button>
								</Box>
							)}
							useOAuthHandlers={useOAuthHandlers}
							useIsUserOAuthed={useIsUserOAuthed}
							isSmartlink={isSmartlink}
							oAuthSource={OAuthSource.searchPageFilter}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
					);
				})}
			</Stack>
		</Box>
	);
};

const MoreAppsButton = ({
	nestedOptions,
	onSelectTab,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
}: {
	nestedOptions: AppOptionType[];
	onSelectTab: (productOrNounKey: ProductKeys | NounKeys | undefined) => void;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}) => {
	const tabAttrs = useTab();
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const renderContent = () => (
		<Hide target="layout" above="sm">
			<MoreAppsMenu
				options={nestedOptions}
				onSelectTab={(value: ProductKeys | NounKeys | undefined) => {
					onSelectTab(value);
					setIsOpen(false);
				}}
				useOAuthHandlers={useOAuthHandlers}
				useIsUserOAuthed={useIsUserOAuthed}
				thirdPartyConfigs={thirdPartyConfigs}
			/>
		</Hide>
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={renderContent}
			placement="bottom-start"
			shouldRenderToParent={true}
			trigger={(triggerProps) => (
				<Pressable
					xcss={[filterTabStyles, tabsTypography]}
					aria-label={formatMessage(messages.accessibleMoreMenuLabel)}
					{...tabAttrs}
					{...triggerProps}
					onClick={() => setIsOpen(!isOpen)}
				>
					<Flex alignItems="center" gap="space.100">
						<FormattedMessage {...messages.more} />
						<ChevronDownIcon color="currentColor" label="" size="small" />
					</Flex>
				</Pressable>
			)}
		/>
	);
};

const splitAppOptions = (
	appOptions: AppOptionType[],
	selectedIndex: number,
): { inlineOptions: AppOptionType[]; nestedOptions: AppOptionType[] } => {
	let inlineOptions = appOptions.slice(0, APP_OPTION_OVERFLOW);

	// if the selected item isn't in the inline options, replace the last item
	if (
		selectedIndex >= 0 &&
		!inlineOptions.find((inline) => inline.value === appOptions[selectedIndex].value)
	) {
		inlineOptions = appOptions.slice(0, APP_OPTION_OVERFLOW - 1);
		inlineOptions.push(appOptions[selectedIndex]);
	}

	// The nestedOptions is then simply the filter of the whole list by what is shown inline
	let inlineValues = inlineOptions.map((i) => i.value);
	const nestedOptions = appOptions.filter((a) => !inlineValues.includes(a.value));

	return {
		inlineOptions,
		nestedOptions,
	};
};

export default AppFilterTabsInline;
